import type { GetOneDiscountResult } from '@/queries/GetOneDiscount';

// Ring generations. Because this mirrors our pages, all generations prior to gen3 are lumped into the gen3 generation.
export const RingGenerations = ['gen3', 'gen4', 'gen4-ceramic'] as const;

// A type representing supported ring generations for a discount code.
export type SupportedRingGeneration = (typeof RingGenerations)[number];

// Price Rule Product Keys
const GEN4_PRODUCT_KEY_PREFIX = 'OuraRing4';
const GEN4_CER_PRODUCT_KEY_PREFIX = 'OuraRing4Cer';
const HORIZON_PRODUCT_KEY_PREFIX = 'Horizon';
const HERITAGE_PRODUCT_KEY_PREFIX = 'Heritage';

// Price Rule categories
const GEN4_CATEGORY = 'gen4-rings';
const GEN4_2024_CATEGORY = 'gen4-2024-rings';
const HORIZON_CATEGORY = 'horizon-rings';
const HERITAGE_CATEGORY = 'heritage-rings';
const RINGS_CATEGORY = 'rings';
const CERAMIC_CATEGORY = 'ceramic-rings';

const getSupportedGenerationsFromProductKeys = (
  productKeys: (string | null)[] | null | undefined,
): Set<SupportedRingGeneration> => {
  const supportedRingGenerations = new Set<SupportedRingGeneration>();

  productKeys
    ?.filter((productKey) => productKey !== null)
    .forEach((productKey) => {
      if (
        productKey.startsWith(HERITAGE_PRODUCT_KEY_PREFIX) ||
        productKey.startsWith(HORIZON_PRODUCT_KEY_PREFIX)
      ) {
        supportedRingGenerations.add('gen3');
      } else if (productKey.startsWith(GEN4_CER_PRODUCT_KEY_PREFIX)) {
        supportedRingGenerations.add('gen4-ceramic');
      } else if (productKey.startsWith(GEN4_PRODUCT_KEY_PREFIX)) {
        supportedRingGenerations.add('gen4');
      }
    });

  return supportedRingGenerations;
};

const getSupportedGenerationsFromCategories = (
  categories: (string | null)[] | null | undefined,
): Set<SupportedRingGeneration> => {
  const supportedRingGenerations = new Set<SupportedRingGeneration>();

  categories
    ?.filter((category) => category !== null)
    .forEach((category) => {
      if (category === HERITAGE_CATEGORY || category === HORIZON_CATEGORY) {
        supportedRingGenerations.add('gen3');
      }
      if (category === CERAMIC_CATEGORY) {
        supportedRingGenerations.add('gen4-ceramic');
      }
      if (category === GEN4_2024_CATEGORY || category === GEN4_CATEGORY) {
        supportedRingGenerations.add('gen4');
      }
      if (category === RINGS_CATEGORY) {
        RingGenerations.map((generation: SupportedRingGeneration) =>
          supportedRingGenerations.add(generation),
        );
      }
    });

  return supportedRingGenerations;
};

// getSupportedDiscountGenerations takes a Discount and returns a set of SupportedRingGeneration.
// This set is the union of all Generations represented in productKeys and categories on the Discount.
export const getSupportedDiscountGenerations = (
  discount: GetOneDiscountResult | null | undefined,
): Set<SupportedRingGeneration> => {
  const priceRules = discount?.content?.discount?.priceRules ?? [];
  let supportedDiscountGenerations = new Set<SupportedRingGeneration>();
  for (const priceRule of priceRules) {
    supportedDiscountGenerations = new Set([
      ...supportedDiscountGenerations,
      ...getSupportedGenerationsFromProductKeys(priceRule?.productKeys),
      ...getSupportedGenerationsFromCategories(priceRule?.categories),
    ]);
  }
  return supportedDiscountGenerations;
};
